import React from 'react';
import {
	Box,
	Button,
	Divider,
	IconButton,
	Paper,
	Tooltip as MuiTooltip,
	Typography,
	Skeleton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { EditIcon, VisibilityIcon } from 'components/icons';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { getCurrentMonthRangeInLocalTime } from 'containers/pages/CANpark/utils/getCurrentMonthRangeInLocalTime';
import CarRegistrationModal from 'containers/pages/CANparkAdmin/components/registerPlates/RegistrationModal';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { getTicketStatsForParkingLocation } from 'api/canpark/tickets/getTicketStatsForParkingLocation';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const fetchTicketStats = async (parkingLocationId: string) => {
	const { startDate, endDate } = getCurrentMonthRangeInLocalTime();
	const stats = await getTicketStatsForParkingLocation(
		parkingLocationId,
		startDate,
		endDate,
	);
	return {
		totalTickets: parseInt(stats.totalTickets),
		logTickets: parseInt(stats.logTickets),
		logAndWarnTickets: parseInt(stats.logAndWarnTickets),
		towTickets: parseInt(stats.towTickets),
	};
};

const ParkingLocationCard: React.FC<{
	parkingLocation: ParkingLocation;
	onClickEdit: (lot: ParkingLocation) => void;
	onClickView: (lot: ParkingLocation) => void;
}> = ({ parkingLocation, onClickEdit, onClickView }) => {
	const {
		data: stats,
		isPending,
		isError,
	} = useQuery({
		queryKey: ['ticketStats', parkingLocation.id],
		queryFn: () => fetchTicketStats(parkingLocation.id),
		staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
	});

	const [openCarRegistrationModal, setOpenCarRegistrationModal] =
		React.useState(false);
	const getTicketTypeMeta = useTicketTypeMeta();

	return (
		<Paper
			elevation={1}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				p: 2,
				width: '100%',
				maxWidth: { xs: '100%', sm: 400 },
				gap: 1,
				transition: (theme) =>
					theme.transitions.create('box-shadow', {
						duration: theme.transitions.duration.shorter,
					}),
				'&:hover': {
					boxShadow: 4,
				},
			}}
		>
			<Typography variant="subtitle1" fontWeight={500}>
				{parkingLocation.name}
			</Typography>
			<Divider flexItem />
			<Box
				sx={{
					display: 'flex',
					gap: 1,
					width: '100%',
					alignItems: 'center',
				}}
			>
				<QueryStatsIcon fontSize="small" color="action" />
				<Typography
					variant="body2"
					color="text.secondary"
					fontWeight={300}
					sx={{ width: '100%' }}
				>
					Stats
				</Typography>
			</Box>

			{/* Fixed-height stats area: loading, error and loaded states differ in
			    height, and letting the card reflow when the async stats resolve
			    shifts the button row below (and any tour popover anchored to it).
			    Reserve a consistent height and match the skeleton to the loaded
			    two-line layout so nothing jumps. */}
			<Box
				sx={{
					width: '100%',
					minHeight: 48,
					display: 'flex',
					flexDirection: 'column',
					gap: 1,
				}}
			>
			{isPending ? (
				// Skeleton loaders for stats (two lines, matching the loaded layout)
				<>
					<Skeleton width="80%" height={20} />
					<Skeleton width="90%" height={20} />
				</>
			) : isError ? (
				<Typography color="error" textAlign="center">
					Failed to load stats
				</Typography>
			) : (
				// Render ticket stats when loaded
				<>
					<Typography
						variant="body2"
						fontWeight={300}
						sx={{ width: '100%' }}
					>
						{stats?.totalTickets} record
						{stats?.totalTickets !== 1 ? 's' : ''} saved this month
					</Typography>
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							width: '100%',
						}}
					>
						<Typography
							variant="body2"
							fontWeight={300}
							color={getTicketTypeMeta('log').textColor}
						>
							{stats?.logTickets}{' '}
							{stats?.logTickets === 1
								? getTicketTypeMeta('log').shortLabel
								: capitalize(getTicketTypeMeta('log').pluralLabel)}
						</Typography>
						<Typography
							variant="body2"
							fontWeight={300}
							color={getTicketTypeMeta('logAndWarn').textColor}
						>
							{stats?.logAndWarnTickets}{' '}
							{stats?.logAndWarnTickets === 1
								? getTicketTypeMeta('logAndWarn').shortLabel
								: capitalize(
										getTicketTypeMeta('logAndWarn').pluralLabel,
								  )}
						</Typography>
						<Typography
							variant="body2"
							fontWeight={300}
							color={getTicketTypeMeta('tow').textColor}
						>
							{stats?.towTickets}{' '}
							{stats?.towTickets === 1
								? getTicketTypeMeta('tow').shortLabel
								: capitalize(getTicketTypeMeta('tow').pluralLabel)}
						</Typography>
					</Box>
				</>
			)}
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
					gap: 1,
					flexWrap: 'wrap',
					mt: 1,
				}}
			>
				<Button
					variant="contained"
					sx={{ borderRadius: '999px' }}
					startIcon={<VisibilityIcon />}
					onClick={() => onClickView(parkingLocation)}
					data-tour="view-site-button"
				>
					View
				</Button>

				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button
						variant="outlined"
						sx={{ borderRadius: '999px' }}
						color="secondary"
						onClick={() => setOpenCarRegistrationModal(true)}
						size="small"
						data-tour="register-plates-button"
					>
						Register Plates
					</Button>

					<MuiTooltip title="Edit Parking Lot">
						<IconButton
							sx={{
								border: 1,
								borderColor: 'divider',
							}}
							onClick={() => onClickEdit(parkingLocation)}
						>
							<EditIcon />
						</IconButton>
					</MuiTooltip>
				</Box>
			</Box>
			<CarRegistrationModal
				open={openCarRegistrationModal}
				setOpen={setOpenCarRegistrationModal}
				parkingLocation={parkingLocation}
			/>
		</Paper>
	);
};

export default ParkingLocationCard;
